import { Localized, useLocalization } from '@fluent/react'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { ButtonGroup, Dialog, Paper, TableContainer } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { loadMailTemplates, selectAllMailTemplates, selectMailTemplates } from './mailTemplatesSlice'
import { MailTemplateDialog } from './MailTemplateDialog'
import { showSuccess } from '../notifications/notificationsSlice'
import { AppId } from '../../app/appTypes'
import { RestoreTemplateDialog } from './RestoreTemplateDialog'
import { LoadingContainer } from '../../app/LoadingContainer'
import { ErrorContainer } from '../../app/ErrorContainer'
import { selectSettings } from '../settings/settingsSlice'
import { enUS, plPL } from '@mui/x-data-grid/locales'
import { QuickSearchToolbar } from '../../app/QuickSearchToolbar'
import { EditIconButton } from '../../app/components/iconButtons/EditIconButton'
import { RestoreIconButton } from '../../app/components/iconButtons/RestoreIconButton'
import { DotColorChip } from '../../app/components/DotColorChip'

type DialogState =
    | { type: "none" }
    | { type: "updateTemplate", templateId: AppId | undefined }
    | { type: "restoreTemplate", templateId: AppId }

   


const DialogContentDispatcher = ({ state, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
    const dispatch = useAppDispatch()
    const onSuccess = useCallback(() => {
        setter({ type: "none" })
        dispatch(showSuccess("saved"))
    }, [setter, dispatch])
    const onCancel = useCallback(() => {
        setter({ type: "none" })
    }, [setter])

    switch (state.type) {
        case "none":
            return null

        case "updateTemplate":
            return <MailTemplateDialog
                id={state.templateId}
                onSaveSuccess={onSuccess}
                onCancel={onCancel}
            />

        case "restoreTemplate":
            return <RestoreTemplateDialog
            id={state.templateId}
            onSaveSuccess={onSuccess}
            onCancel={onCancel}
            />
    }
}

export const MailTemplates = () => {
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const { state: mailTemplatesState } = useAppSelector(selectMailTemplates)
    const rows = useAppSelector(state => selectAllMailTemplates(state))

    const [dialog, setDialog] = useState<DialogState>({ type: "none" })

    const handleEditTemplate = useCallback((templateId: AppId) => {
        setDialog({
            type: "updateTemplate",
            templateId
        })
    }, [])

    const handleRestoreTemplate = useCallback((templateId: AppId) => {
        setDialog({
            type: "restoreTemplate",
            templateId
        })
    }, [setDialog])


    useEffect(() => {
        if (mailTemplatesState.type === "empty") {
            dispatch(loadMailTemplates())
        }
    }, [dispatch, mailTemplatesState])
    
    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const columns: GridColDef[] = [
            {
                field: 'name',
                headerName: l10n.getString("mail-template-name", null, "Nazwa"),
                flex: 1,
                valueGetter: (_, row) => {
                    return l10n.getString(`mail-type-${row.type}`, null, `_${row.type}`)
                }
            },
            {
                field: 'context',
                headerName: l10n.getString("mail-template-category", null, "_Kategoria"),
                flex: 1,
                valueGetter: (_, row) => {
                    return row.context ? l10n.getString(`mail-template-category-${row.context}`, null, `_${row.context}`) : '';
                }
            },
            {
                field: 'isCompanyTemplate',
                headerName: l10n.getString("mail-template-version", null, "_Wersja szablonu"),
                flex: 1,
                renderCell: (params) => {
                    return <DotColorChip l10nId={params.row.companyId ? "mail-template-own" : "mail-template-system"} label={params.row.companyId ? "_Własna" : "_Systemowa"} color={params.row.companyId ? "success" : "error"} />
                }
            },
            {
                field: 'actions', type: 'actions', width: 150,
                renderCell: (params) => {
                    return (
                        <ButtonGroup>
                            { params.row.companyId ?
                                (<RestoreIconButton
                                    tooltip={<Localized id="mail-restore-system">Przywróć systemowy</Localized>}
                                    onClick={() => handleRestoreTemplate(params.row.id)}
                                />) : null
                            }
                            <EditIconButton
                                tooltip={<Localized id="edit">Edytuj</Localized>}
                                onClick={() => handleEditTemplate(params.row.id)}
                            />
                        </ButtonGroup>
                    )
                }
            }
        ];


    switch (mailTemplatesState.type) {
        case "empty":
        case "loading":
            return <LoadingContainer/>
        case "error":
            return <ErrorContainer error={mailTemplatesState.error}/>;
        case "loaded":
            return (
                <>
                    <TableContainer component={Paper}>
                        <DataGrid style={{ minHeight: 100, height: 'calc(100vh - 210px)' }} localeText={getCustomLocaleText()} rows={rows} columns={columns} initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }} onRowDoubleClick={({ row }) => handleEditTemplate(row.id)} slots={{ toolbar: QuickSearchToolbar }} />
                    </TableContainer>
                    <Dialog open={dialog.type !== "none"} fullScreen={dialog.type === "restoreTemplate" ? false : true} keepMounted={false}>
                        <DialogContentDispatcher state={dialog} setter={setDialog} />
                    </Dialog>
                </>
            )
    }
}
